/**
 * 过滤行功能内的下拉条件
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Button from '../../wui-button/src';
import { prefix } from "../../wui-core/src/index";
import Dropdown from '../../wui-dropdown/src';
import Icon from '../../wui-icon/src';
import Menu from '../../wui-menu/src';
import i18n from './lib/i18n';
import { getLangInfo } from "../../wui-locale/src/tool";
import { IFilterDropDownProps } from './iTable';
import { FilterDropdownKeysStrType, FilterDropdownKeysNumType } from './interface';

const { Item } = Menu;

const defaultProps = {
    isShowCondition: 'show',
    filterDropdownType: 'string'
}

const FilterDropDown: React.FC<IFilterDropDownProps> = (props) => {
    const {
        isShowCondition = defaultProps.isShowCondition,
        filterDropdownType = defaultProps.filterDropdownType,
        conditionValue,
        onSelectDropdown,
        onClickClear,
        filterDropdownIncludeKeys,
        isShowClear,
        fieldid,
        dir: direction,
        locale
    } = props;

    const [selectValue, setSelectValue] = useState<string[]>([conditionValue || 'LIKE']);
    const [selectNumberValue, setSelectNumberValue] = useState<string[]>([conditionValue || 'EQ']);

    // 更新条件值
    useEffect(() => {
        setSelectValue([conditionValue || 'LIKE']);
        setSelectNumberValue([conditionValue || 'EQ']);
    }, [conditionValue]);

    /**
     * 点击下拉菜单
     *
     * @param {*} item 选中的selectRecord
     */
    const handleSelectDropdown = useCallback((item: Record<string, any>) => {
        if (onSelectDropdown) {
            if (filterDropdownType === 'string') {
                setSelectValue([item.key]);
                onSelectDropdown(item);
            }
            if (filterDropdownType === 'number') {
                setSelectNumberValue([item.key]);
                onSelectDropdown(item);
            }
        }
    }, [onSelectDropdown, filterDropdownType]);

    /**
     * 清除事件
     */
    const handleClickClear = useCallback(() => {
        if (onClickClear) {
            // 注意：原代码中setState没有实际更新状态，保持这个行为
            onClickClear();
        }
    }, [onClickClear]);

    /**
     * 根据props来获得指定的Menu,分为String和Number
     *
     * @returns JSX Menu
     */
    const getMenu = useMemo(() => {
        const currentLocale = getLangInfo(locale, i18n, 'table');
        const stringEnum: Record<string, string> = {
            LIKE: 'include',
            ULIKE: 'exclusive',
            EQ: 'equal',
            UEQ: 'unequal',
            START: 'begin',
            END: 'end'
        };
        const numberEnum: Record<string, string> = {
            GT: 'greaterThan',
            GTEQ: 'greatThanEqualTo',
            LT: 'lessThan',
            LTEQ: 'lessThanEqualTo',
            EQ: 'beEqualTo',
            UEQ: 'notEqualTo'
        };
        const _local = Object.keys(currentLocale.langMap).length ? currentLocale.langMap : i18n;

        if (filterDropdownIncludeKeys !== undefined) {
            switch (filterDropdownType) {
                case 'string':
                    return (
                        <Menu
                            onClick={handleSelectDropdown}
                            selectedKeys={selectValue}
                            dir={direction}
                        >
                            {(filterDropdownIncludeKeys as FilterDropdownKeysStrType[]).map((item: string) => {
                                return <Item key={item}>{_local[stringEnum[item]]}</Item>
                            })}
                        </Menu>
                    );
                case 'number':
                    return (
                        <Menu
                            onClick={handleSelectDropdown}
                            selectedKeys={selectNumberValue}
                            dir={direction}
                        >
                            {(filterDropdownIncludeKeys as FilterDropdownKeysNumType[]).map((item: string) => {
                                return <Item key={item}>{_local[numberEnum[item]]}</Item>
                            })}
                        </Menu>
                    );
                default:
                    return <div></div>;
            }
        } else {
            switch (filterDropdownType) {
                case 'string':
                    return (
                        <Menu
                            onClick={handleSelectDropdown}
                            selectedKeys={selectValue}
                            dir={direction}
                        >
                            <Item key="LIKE">{currentLocale.langMap.include || '包含'}</Item>
                            <Item key="ULIKE">{currentLocale.langMap.exclusive || '不包含'}</Item>
                            <Item key="EQ">{currentLocale.langMap.equal || '等于'}</Item>
                            <Item key="UEQ">{currentLocale.langMap.unequal || '不等于'}</Item>
                            <Item key="RLIKE">{currentLocale.langMap.begin || '以开始'}</Item>
                            <Item key="LLIKE">{currentLocale.langMap.end || '以结尾'}</Item>
                        </Menu>
                    );
                case 'number':
                    return (
                        <Menu
                            onClick={handleSelectDropdown}
                            selectedKeys={selectNumberValue}
                            dir={direction}
                        >
                            <Item key="GT">{currentLocale.langMap.greaterThan || '大于'}</Item>
                            <Item key="GTEQ">{currentLocale.langMap.greatThanEqualTo || '大于等于'}</Item>
                            <Item key="LT">{currentLocale.langMap.lessThan || '小于'}</Item>
                            <Item key="LTEQ">{currentLocale.langMap.lessThanEqualTo || '小于等于'}</Item>
                            <Item key="EQ">{currentLocale.langMap.beEqualTo || '等于'}</Item>
                            <Item key="UEQ">{currentLocale.langMap.notEqualTo || '不等于'}</Item>
                        </Menu>
                    );
                default:
                    return <div></div>;
            }
        }
    }, [filterDropdownType, filterDropdownIncludeKeys, selectValue, selectNumberValue, direction, locale, handleSelectDropdown]);

    const fieldidDropdownAttr = fieldid ? { fieldid: `${fieldid}-filter` } : {};
    const fieldidClearAttr = fieldid ? { fieldid: `${fieldid}-clear` } : {};

    return (
        <div className="filter-btns">
            {isShowCondition === 'show' && (
                <Dropdown
                    overlayClassName={`${prefix}-filter-dropdown-menu-wrap`}
                    trigger={['click']}
                    overlay={getMenu}
                    animation="slide-up"
                    dir={direction}
                >
                    <Button
                        shape="border"
                        dir={direction}
                        style={{ marginLeft: "2px", minWidth: "0px", width: "26px", lineHeight: "24px", padding: 0 }}
                        {...fieldidDropdownAttr}
                    >
                        <Icon style={{ padding: 0, color: '#585858' }} type="uf-filter" />
                    </Button>
                </Dropdown>
            )}
            <Button
                onClick={handleClickClear}
                shape="border"
                dir={direction}
                style={{
                    marginLeft: "2px",
                    minWidth: "0px",
                    width: "26px",
                    lineHeight: "24px",
                    padding: 0,
                    visibility: isShowClear || selectValue.length > 0 ? "visible" : "hidden"
                }}
                {...fieldidClearAttr}
            >
                <Icon
                    style={{
                        padding: 0,
                        color: '#585858',
                        visibility: isShowClear || selectValue.length > 0 ? "visible" : "hidden"
                    }}
                    type="uf-filterno"
                />
            </Button>
        </div>
    );
};

export default FilterDropDown;